<template>
  <div>
    <h2 class="font-header text-[3rem] font-bold text-primary mb-6">Account Management</h2>

    <div v-if="loading" class="text-center py-8">
      <Loader size="large" />
    </div>

    <div v-else class="bg-white rounded-lg shadow-md overflow-hidden">
      <div class="w-full overflow-x-auto">
        <table class="w-full min-w-max">
          <thead class="bg-green-700 text-white">
            <tr>
              <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Email</th>
              <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">First Name</th>
              <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Last Name</th>
              <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Phone</th>
              <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Location</th>
              <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Created</th>
              <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Status</th>
              <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="users.length === 0">
              <td colspan="8" class="px-4 py-8 text-center font-body text-body text-gray-600">
                No users found
              </td>
            </tr>
            <tr v-for="user in users" :key="user.id" class="border-b border-creme-200 hover:bg-creme-50">
              <td class="px-4 py-3 font-body text-body text-gray-900">{{ user.email }}</td>
              <td class="px-4 py-3 font-body text-body text-gray-900">{{ user.first_name || '-' }}</td>
              <td class="px-4 py-3 font-body text-body text-gray-900">{{ user.last_name || '-' }}</td>
              <td class="px-4 py-3 font-body text-body text-gray-900">{{ user.phone_number || '-' }}</td>
              <td class="px-4 py-3 font-body text-body text-gray-900">{{ getUserLocation(user) }}</td>
              <td class="px-4 py-3 font-body text-body text-gray-900">{{ formatDateShort(user.created_at) }}</td>
              <td class="px-4 py-3 font-body text-body">
                <span v-if="user.is_banned" class="text-red-600 font-semibold">Banned</span>
                <span v-else-if="user.is_frozen" class="text-yellow-600 font-semibold">
                  Frozen
                  <span v-if="user.freeze_until" class="text-xs block">
                    Until {{ formatDateShort(user.freeze_until) }}
                  </span>
                </span>
                <span v-else class="text-green-600">Active</span>
              </td>
              <td class="px-4 py-3">
                <button @click="openBanModal(user)" :disabled="isCurrentUser(user.id)" :class="[
                  'mr-2 px-3 py-1 rounded text-sm font-marketing',
                  isCurrentUser(user.id)
                    ? 'bg-gray-400 text-white cursor-not-allowed opacity-60'
                    : user.is_banned
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-red-600 text-white hover:bg-red-700'
                ]">
                  {{ user.is_banned ? 'Unban' : 'Ban' }}
                </button>
                <button @click="openFreezeModal(user)" :disabled="isCurrentUser(user.id)" :class="[
                  'px-3 py-1 rounded text-sm font-marketing',
                  isCurrentUser(user.id)
                    ? 'bg-gray-400 text-white cursor-not-allowed opacity-60'
                    : user.is_frozen
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-yellow-600 text-white hover:bg-yellow-700'
                ]">
                  {{ user.is_frozen ? 'Unfreeze' : 'Freeze' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="pagination && pagination.total_pages > 1"
        class="p-4 flex justify-between items-center border-t border-creme-200">
        <button @click="loadUsers(currentPage - 1)" :disabled="!pagination.prev_page"
          :class="['px-4 py-2 rounded-md font-marketing', pagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
          Previous
        </button>
        <span class="font-marketing text-gray-700">
          Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
        </span>
        <button @click="loadUsers(currentPage + 1)" :disabled="!pagination.next_page"
          :class="['px-4 py-2 rounded-md font-marketing', pagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
          Next
        </button>
      </div>
    </div>

    <BaseModal v-model="banModal.show" content-class="max-w-md">
      <h3 class="text-xl font-marketing font-bold mb-4">
        {{ banModal.user.is_banned ? 'Unban User' : 'Ban User' }}
      </h3>
      <div v-if="!banModal.user.is_banned" class="mb-4">
        <label class="block text-sm font-marketing font-medium text-gray-700 mb-2">
          Reason for ban
        </label>
        <textarea v-model="banModal.reason"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
          rows="3" placeholder="Enter reason for banning this user..."></textarea>
      </div>
      <div class="flex justify-end gap-3">
        <button @click="banModal.show = false"
          class="px-4 py-2 text-sm font-marketing border border-gray-300 rounded-md hover:bg-gray-50">
          Cancel
        </button>
        <button @click="confirmBan"
          class="px-4 py-2 text-sm font-marketing bg-red-600 text-white rounded-md hover:bg-red-700">
          {{ banModal.user.is_banned ? 'Unban' : 'Ban' }}
        </button>
      </div>
    </BaseModal>

    <BaseModal v-model="freezeModal.show" content-class="max-w-md">
      <h3 class="text-xl font-marketing font-bold mb-4">
        {{ freezeModal.user.is_frozen ? 'Unfreeze User' : 'Freeze User' }}
      </h3>
      <div v-if="!freezeModal.user.is_frozen" class="space-y-4">
        <div>
          <label class="block text-sm font-marketing font-medium text-gray-700 mb-2">
            Reason for freeze
          </label>
          <textarea v-model="freezeModal.reason"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
            rows="3" placeholder="Enter reason for freezing this user..."></textarea>
        </div>
        <div>
          <label class="block text-sm font-marketing font-medium text-gray-700 mb-2">
            Freeze Duration
          </label>
          <select v-model="freezeModal.durationType"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black mb-2">
            <option value="duration">For a duration</option>
            <option value="until">Until a specific date</option>
            <option value="indefinite">Indefinitely</option>
          </select>
          <input v-if="freezeModal.durationType === 'duration'" v-model.number="freezeModal.durationDays" type="number"
            min="1"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
            placeholder="Number of days" />
          <input v-if="freezeModal.durationType === 'until'" v-model="freezeModal.untilDate" type="datetime-local"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black" />
        </div>
      </div>
      <div class="flex justify-end gap-3 mt-6">
        <button @click="freezeModal.show = false"
          class="px-4 py-2 text-sm font-marketing border border-gray-300 rounded-md hover:bg-gray-50">
          Cancel
        </button>
        <button @click="confirmFreeze"
          class="px-4 py-2 text-sm font-marketing bg-yellow-600 text-white rounded-md hover:bg-yellow-700">
          {{ freezeModal.user.is_frozen ? 'Unfreeze' : 'Freeze' }}
        </button>
      </div>
    </BaseModal>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { adminService } from '../../services/admin'
import { authService } from '../../services/auth'
import Loader from '../Loader.vue'
import BaseModal from './BaseModal.vue'
import { useLoadingState } from '../../composables/useLoadingState'
import { formatDateShort } from '../../utils/date'

const users = ref([])
const currentUserId = ref(null)
const { loading, setLoading } = useLoadingState(false)
const currentPage = ref(1)
const limit = ref(10)
const pagination = ref(null)

const banModal = ref({
  show: false,
  user: null,
  reason: ''
})

const freezeModal = ref({
  show: false,
  user: null,
  reason: '',
  durationType: 'duration',
  durationDays: 7,
  untilDate: ''
})

const loadUsers = async (page = 1) => {
  setLoading(true)
  try {
    const response = await adminService.getUsers(page, limit.value)
    if (response.data && response.pagination) {
      users.value = response.data || []
      pagination.value = response.pagination
      currentPage.value = page
    } else {
      users.value = []
      pagination.value = null
    }
  } catch (error) {
    console.error('Failed to load users:', error)
    users.value = []
    pagination.value = null
  } finally {
    setLoading(false)
  }
}

const isCurrentUser = (userId) => {
  return currentUserId.value !== null && currentUserId.value === userId
}

const getUserLocation = (user) => {
  if (user.shipping_address) {
    const addr = user.shipping_address
    const parts = []
    if (addr.city) parts.push(addr.city)
    if (addr.state) parts.push(addr.state)
    if (addr.country) parts.push(addr.country)
    return parts.join(', ') || '-'
  }
  return '-'
}

const openBanModal = (user) => {
  if (isCurrentUser(user.id)) return
  banModal.value = {
    show: true,
    user,
    reason: user.ban_reason || ''
  }
}

const openFreezeModal = (user) => {
  if (isCurrentUser(user.id)) return
  freezeModal.value = {
    show: true,
    user,
    reason: user.freeze_reason || '',
    durationType: user.freeze_until ? 'until' : 'duration',
    durationDays: 7,
    untilDate: user.freeze_until ? new Date(user.freeze_until).toISOString().slice(0, 16) : ''
  }
}

const confirmBan = async () => {
  try {
    const updateData = {
      is_banned: !banModal.value.user.is_banned
    }

    if (!banModal.value.user.is_banned && banModal.value.reason) {
      updateData.ban_reason = banModal.value.reason
    }

    await adminService.updateAccountStatus(banModal.value.user.id, updateData)
    banModal.value.show = false
    await loadUsers()
  } catch (error) {
    console.error('Failed to update ban status:', error)
    alert('Failed to update ban status. Please try again.')
  }
}

const confirmFreeze = async () => {
  try {
    const updateData = {
      is_frozen: !freezeModal.value.user.is_frozen
    }

    if (!freezeModal.value.user.is_frozen) {
      if (freezeModal.value.reason) {
        updateData.freeze_reason = freezeModal.value.reason
      }

      if (freezeModal.value.durationType === 'duration') {
        const untilDate = new Date()
        untilDate.setDate(untilDate.getDate() + freezeModal.value.durationDays)
        updateData.freeze_until = untilDate.toISOString()
      } else if (freezeModal.value.durationType === 'until' && freezeModal.value.untilDate) {
        updateData.freeze_until = new Date(freezeModal.value.untilDate).toISOString()
      }
    }

    await adminService.updateAccountStatus(freezeModal.value.user.id, updateData)
    freezeModal.value.show = false
    await loadUsers()
  } catch (error) {
    console.error('Failed to update freeze status:', error)
    alert('Failed to update freeze status. Please try again.')
  }
}

onMounted(async () => {
  const user = authService.getStoredUser()
  if (user?.id) {
    currentUserId.value = user.id
  } else {
    const fetchedUser = await authService.getCurrentUser()
    if (fetchedUser?.id) {
      currentUserId.value = fetchedUser.id
    }
  }
  await loadUsers(1)
})
</script>
